// CSP-2788 T4 (decisione n.20, ANALISI-NOTTURNA-CSP-2788/10-fuso-orario.md §6) - Date degli eventi catastrofali:
// sul filo istanti UTC (ISO-8601 con `Z`, oppure epoch millis), a video e nei picker l'ora di Roma.
// Funzioni pure e indipendenti dal fuso del browser: lo stesso risultato con il processo in UTC o a Roma.
import moment from 'moment-timezone';

export const FUSO_ROMA = 'Europe/Rome';

/** Formato degli istanti mandati al BE: lo stesso che emette os-sinistri (`yyyy-MM-dd'T'HH:mm:ss'Z'`). */
export const FORMATO_ISTANTE_UTC = 'YYYY-MM-DDTHH:mm:ss[Z]';

/** Data e ora a video (AFU: GG/MM/AAAA HH:MM). */
export const FORMATO_DATA_ORA_VIDEO = 'DD/MM/YYYY HH:mm';

const FORMATO_ORA_MURO = 'YYYY-MM-DDTHH:mm:ss';
const FORMATO_GIORNO = 'YYYY-MM-DD';

const assente = (valore) => valore === null || valore === undefined || valore === '';

/**
 * Un istante del BE come moment in ora di Roma; null se assente o non valido.
 * - stringa ISO-8601 con `Z` o offset: l'istante; senza offset: ora di Roma, la stessa regola di
 *   `FusoOrarioEvento.istante` di os-sinistri (copre le risposte di prima del rilascio del BE);
 * - numero: epoch millis (anagrafica evento, lista della dashboard);
 * - Date: l'istante.
 */
export const momentRoma = (valore) => {
  if (assente(valore)) return null;
  const m = typeof valore === 'string'
    ? moment.tz(valore, moment.ISO_8601, FUSO_ROMA)
    : moment.tz(valore, FUSO_ROMA);
  return m.isValid() ? m : null;
};

/** Testo a video in ora di Roma; '' se assente o non valido. */
export const formattaOraRoma = (valore, formato = FORMATO_DATA_ORA_VIDEO) => {
  const m = momentRoma(valore);
  return m ? m.format(formato) : '';
};

/** Un istante come stringa ISO UTC con `Z` (parametri e body verso il BE); null se assente o non valido. */
export const istanteUtc = (valore) => {
  const m = momentRoma(valore);
  return m ? m.clone().utc().format(FORMATO_ISTANTE_UTC) : null;
};

/** I campi locali di una Date (come li mostra un picker Kendo) come ora di Roma; null se non valida. */
const oraDiRomaDaCampiLocali = (data, formato) => {
  if (assente(data)) return null;
  const locale = moment(data);
  if (!locale.isValid()) return null;
  return moment.tz(locale.format(formato), formato, FUSO_ROMA);
};

/**
 * CSP-2788 (review n.7) - Istante d'origine delle Date create da dataPickerDaIstante. I campi locali di una Date non
 * distinguono le due 02:30 dell'ultima domenica di ottobre: senza questo ricordo un istante non toccato dall'utente
 * tornerebbe al BE un'ora prima. WeakMap: nessun campo aggiunto alla Date, nessuna perdita di memoria.
 * Valore: `{ tempo, istante }`, con `tempo` = getTime() alla creazione (una Date modificata sul posto non vale piu').
 */
const istantiDeiPicker = new WeakMap();

/**
 * Istante -> Date per i picker Kendo, che mostrano i campi LOCALI della Date: i campi sono l'ora di Roma, cosi'
 * il picker mostra l'ora di Roma qualunque sia il fuso del browser (con il browser a Roma e' l'istante stesso).
 */
export const dataPickerDaIstante = (valore) => {
  const m = momentRoma(valore);
  if (!m) return null;
  const data = new Date(m.year(), m.month(), m.date(), m.hours(), m.minutes(), m.seconds());
  istantiDeiPicker.set(data, { tempo: data.getTime(), istante: m.clone().utc().format(FORMATO_ISTANTE_UTC) });
  return data;
};

/**
 * Date di un picker (campi locali = ora di Roma) -> istante UTC con `Z` per il BE. Ora di Roma che non esiste
 * (29/03 02:30): avanti di un'ora; ora doppia (25/10 02:30): la prima, in ora legale. E' la regola di
 * `LocalDateTime#atZone` che os-sinistri applica alle date senza offset. Una Date arrivata da dataPickerDaIstante e
 * non modificata (stesso oggetto, stesso valore) torna all'istante d'origine, anche nell'ora doppia.
 */
export const istanteDaDataPicker = (data) => {
  const origine = data instanceof Date ? istantiDeiPicker.get(data) : undefined;
  if (origine && origine.tempo === data.getTime()) return origine.istante;
  const roma = oraDiRomaDaCampiLocali(data, FORMATO_ORA_MURO);
  return roma ? roma.utc().format(FORMATO_ISTANTE_UTC) : null;
};

/**
 * Giorno di calendario scelto in un DatePicker -> la mezzanotte di Roma di quel giorno, come istante UTC con `Z`.
 * os-sinistri ne ricava il giorno di Roma ("evento attivo nel giorno X"); l'ora del picker si ignora.
 */
export const inizioGiornoRomaDaDataPicker = (data) => {
  const roma = oraDiRomaDaCampiLocali(data, FORMATO_GIORNO);
  return roma ? roma.utc().format(FORMATO_ISTANTE_UTC) : null;
};
